/**
 * IS THIS LEAD SOMEWHERE THE SHOP CANNOT GO.
 *
 * Anthony, 2026-10-08: "this morning brian huey got a california lead in
 * corona, he's in north carolina." A customer in Corona, CA rang
 * Supercanic, got our missed-call text, then texted his name, address and
 * truck to Huey's Mobile's line instead. The desk filed it where it landed,
 * which is correct, and nothing on the card said "this person is 2,300
 * miles away". Brian turned it into a quote.
 *
 * This is a FLAG, never a block. It routes nothing and hides nothing. It
 * puts one line on the card so whoever opens it looks before quoting.
 *
 * ─── TWO SIGNALS, ONE TEST ─────────────────────────────────────────────
 *
 *  1. A state named in what they wrote ("corona ca", "Phoenix, AZ",
 *     "north carolina").
 *  2. The area code of their number.
 *
 * Each signal gives a time zone, and the lead is flagged only when that
 * zone is TWO OR MORE HOURS from the shop's own. Not "a different state":
 * Supercanic has a Lake Havasu shop across the Arizona line, Swift works
 * Vegas next to Arizona, and a neighbour state one hour off is normal
 * work. California against North Carolina is three hours, and so is every
 * case worth stopping for. Ported numbers keep their old code, which is
 * why the text wins when both are present and the card says which it saw.
 *
 * ─── WHY ONLY SOME TWO-LETTER CODES ────────────────────────────────────
 *
 * "in", "or", "me", "ok", "hi", "oh", "la", "pa", "co", "de" and the rest
 * are ordinary words in a text message, and "ct" is a court in an address (the Corona lead was on Silverton ct). Only codes that are not English
 * words are read as a state. Full state names that are also first names or
 * street names (Georgia, Virginia, Washington, Carolina alone) are left out.
 *
 * Pure, no imports beyond the area-code table, so it runs in the browser
 * and in a test.
 */

import { ET, CT, MT, AZ, PT, AK, HI, zoneForPhone } from "./areacodes.ts";

/** Standard-time UTC offset in hours, enough to measure a gap. */
const OFFSET: Record<string, number> = {
  [ET]: -5, [CT]: -6, [MT]: -7, [AZ]: -7, [PT]: -8, [AK]: -9, [HI]: -10,
};

const ZONE_LABEL: Record<string, string> = {
  [ET]: "Eastern", [CT]: "Central", [MT]: "Mountain", [AZ]: "Arizona",
  [PT]: "Pacific", [AK]: "Alaska", [HI]: "Hawaii",
};

/** Two-letter codes that are not English words, read as a state. */
const CODE_ZONE: Record<string, string> = {
  AZ, CA: PT, NV: PT, WA: PT, NM: MT, WY: MT, UT: MT,
  TX: CT, IL: CT, IA: CT, KS: CT, WI: CT, MN: CT, ND: CT, SD: CT, TN: CT, AR: CT,
  FL: ET, NY: ET, NJ: ET, NC: ET, SC: ET, GA: ET, KY: ET, VA: ET, WV: ET,
  RI: ET, NH: ET, VT: ET, MD: ET,
  AK,               // not HI: "hi" is a greeting
};

/** Full names, longest first so "west virginia" beats "virginia". */
const NAME_ZONE: Array<[string, string]> = [
  ["north carolina", ET], ["south carolina", ET], ["west virginia", ET],
  ["north dakota", CT], ["south dakota", CT], ["new hampshire", ET],
  ["new jersey", ET], ["new york", ET], ["new mexico", MT],
  ["rhode island", ET], ["massachusetts", ET], ["pennsylvania", ET],
  ["connecticut", ET], ["california", PT], ["mississippi", CT],
  ["tennessee", CT], ["wisconsin", CT], ["minnesota", CT], ["louisiana", CT],
  ["oklahoma", CT], ["nebraska", CT], ["kentucky", ET], ["michigan", ET],
  ["maryland", ET], ["delaware", ET], ["colorado", MT], ["illinois", CT],
  ["missouri", CT], ["arkansas", CT], ["alabama", CT], ["arizona", AZ],
  ["florida", ET], ["indiana", ET], ["vermont", ET], ["montana", MT],
  ["wyoming", MT], ["nevada", PT], ["oregon", PT], ["kansas", CT],
  ["alaska", AK], ["hawaii", HI], ["texas", CT], ["idaho", MT],
  ["maine", ET], ["iowa", CT], ["ohio", ET], ["utah", MT],
];

/** The state a message names, as [what it said, zone], or null. */
export function stateInText(text: string): [string, string] | null {
  const t = ` ${text.toLowerCase().replace(/[^a-z0-9]+/g, " ")} `;
  for (const [name, zone] of NAME_ZONE) {
    if (t.includes(` ${name} `)) return [name.replace(/\b\w/g, (c) => c.toUpperCase()), zone];
  }
  for (const m of t.matchAll(/ ([a-z]{2})(?= )/g)) {
    const code = m[1]!.toUpperCase();
    const zone = CODE_ZONE[code];
    if (zone) return [code, zone];
  }
  return null;
}

export type OutOfArea = { why: string; zone: string };

/**
 * The one line for the card, or null when the lead looks local or when
 * nothing can be told. `shopZone` is accounts.timezone; a shop with no
 * readable zone gets no flag rather than a guess.
 */
export function outOfArea(
  lead: { text: Array<string | null | undefined>; phone: string | null | undefined },
  shopZone: string | null | undefined,
): OutOfArea | null {
  const shop = OFFSET[String(shopZone ?? "")];
  if (shop === undefined) return null;

  const said = stateInText(lead.text.filter(Boolean).join(" \n "));
  const numZone = zoneForPhone(lead.phone);
  const code = String(lead.phone ?? "").replace(/\D/g, "").slice(-10).slice(0, 3);

  // What they wrote beats where the number was issued.
  const zone = said?.[1] ?? numZone;
  if (!zone) return null;
  const off = OFFSET[zone];
  if (off === undefined || Math.abs(off - shop) < 2) return null;

  const bits: string[] = [];
  if (said) bits.push(`says ${said[0]}`);
  if (numZone && OFFSET[numZone] !== undefined && Math.abs(OFFSET[numZone]! - shop) >= 2) {
    bits.push(`${code} is a ${ZONE_LABEL[numZone]} number`);
  }
  return {
    zone,
    why: `Out of area? ${bits.join(", ")}. This shop is on ${ZONE_LABEL[String(shopZone)]} time.`,
  };
}
